import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Users, Plus, Copy, Check, MessageSquare, ShieldCheck, Trash2, Briefcase, UserPlus, Clock,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Team management panel — drop into any Org dashboard.
 * Lists current staff + pending invites; lets the owner mint
 * WhatsApp-shareable invite links scoped to a specific role.
 */
export default function OrgTeamPanel({ org }) {
  const [staff, setStaff]       = useState([]);
  const [invites, setInvites]   = useState([]);
  const [roles, setRoles]       = useState([]);
  const [activeIds, setActiveIds] = useState(new Set());
  const [open, setOpen]         = useState(false);

  const orgId = org?.id;
  const isOwner = (org?.viewer_role === "owner") || true; // owner page renders this

  const refresh = async () => {
    if (!orgId) return;
    const [s, i, r] = await Promise.all([
      api.get(`/orgs/${orgId}/staff`).catch(() => ({ data: { items: [] } })),
      api.get(`/orgs/${orgId}/invites`).catch(() => ({ data: { items: [] } })),
      api.get(`/orgs/${orgId}/invite-roles`).catch(() => ({ data: { roles: [] } })),
    ]);
    setStaff(s.data.items || []);
    setInvites(i.data.items || []);
    setRoles(r.data.roles || []);
    // Presence dots — batch check who's finalized on a project this week.
    const ids = (s.data.items || []).map((u) => u.id).filter(Boolean);
    if (ids.length) {
      try {
        const { data } = await api.get(`/users/recently-active?ids=${ids.join(",")}`);
        setActiveIds(new Set(data.active_ids || []));
      } catch (err) {
        console.warn("[OrgTeamPanel] recently-active presence check failed:", err?.message);
      }
    } else {
      setActiveIds(new Set());
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const pending = invites.filter((x) => x.status === "pending");

  return (
    <section className="cs-card rounded-2xl p-6" data-testid="org-team-panel">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.28em] uppercase text-white/55 flex items-center gap-2">
            <Users size={11} /> Team
          </div>
          <div className="font-display text-2xl uppercase tracking-tight mt-1">
            {staff.length} on the team
            {pending.length > 0 && (
              <span className="ml-3 text-sm text-white/55">· {pending.length} pending</span>
            )}
          </div>
        </div>
        {isOwner && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="h-9 px-4 rounded-full text-[10px] uppercase tracking-[0.22em] btn-themed"
                data-testid="invite-staff-btn">
                <UserPlus size={12} className="mr-1.5" /> Invite staff
              </Button>
            </DialogTrigger>
            <InviteDialog
              org={org}
              roles={roles}
              onClose={() => setOpen(false)}
              onCreated={refresh}
            />
          </Dialog>
        )}
      </header>

      {/* Staff list */}
      <ul className="mt-5 space-y-2" data-testid="staff-list">
        {staff.map((u) => {
          const online = activeIds.has(u.id);
          return (
          <li key={u.id}
              data-testid={`staff-${u.id}`}
              className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg bg-white/[0.03] border border-white/10">
            <div className="flex items-center gap-3 min-w-0">
              <div className="relative">
                <div className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-[11px] font-display">
                  {(u.name || u.email).slice(0, 2).toUpperCase()}
                </div>
                {online && (
                  <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-black bg-emerald-400"
                        title="Currently on an active project block"
                        data-testid={`presence-dot-${u.id}`} />
                )}
              </div>
              <div className="min-w-0">
                <div className="text-sm truncate">{u.name}</div>
                <div className="text-[10px] tracking-[0.18em] uppercase text-white/45 truncate flex items-center gap-1.5">
                  {u.email}
                  {online && (
                    <span className="text-emerald-300 tracking-[0.22em]">
                      · ON A FLOOR
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-[10px] tracking-[0.22em] uppercase text-white/85">{u.role_label}</div>
              <div className="text-[9px] tracking-[0.18em] uppercase text-white/40 mt-0.5">
                {u.engagement_type || "staff"}{u.is_owner && " · owner"}
              </div>
            </div>
          </li>
          );
        })}
        {staff.length === 0 && (
          <li className="text-sm text-white/45 px-1 py-3">No team members yet.</li>
        )}
      </ul>

      {/* Pending invites */}
      {pending.length > 0 && (
        <>
          <div className="mt-7 text-[10px] tracking-[0.3em] uppercase text-white/45 flex items-center gap-2">
            <Clock size={11} /> Pending Invites
          </div>
          <ul className="mt-3 space-y-2" data-testid="pending-invites">
            {pending.map((inv) => (
              <PendingInviteRow key={inv.id} inv={inv} orgId={orgId} onChanged={refresh} />
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

function PendingInviteRow({ inv, orgId, onChanged }) {
  const [copied, setCopied] = useState(false);
  const fullUrl = inv.claim_url?.startsWith("http")
    ? inv.claim_url
    : `${window.location.origin}${inv.claim_url}`;
  const waText  = `You've been invited to join ${inv.role_label} on Call Sheet.\nOpen this link to set up your account:\n${fullUrl}`;
  const waUrl   = `https://wa.me/${(inv.invitee_phone || "").replace(/[^0-9]/g, "")}?text=${encodeURIComponent(waText)}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      toast.success("Invite link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Copy failed");
    }
  };

  const revoke = async () => {
    if (!window.confirm(`Revoke invite to ${inv.invitee_name || inv.role_label}?`)) return;
    try {
      await api.delete(`/orgs/${orgId}/invites/${inv.id}`);
      toast.success("Invite revoked");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Revoke failed");
    }
  };

  return (
    <li className="px-3 py-3 rounded-lg bg-white/[0.025] border border-white/10"
        data-testid={`invite-${inv.id}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="text-sm truncate">
            {inv.invitee_name || <span className="text-white/55 italic">Unnamed</span>}
            <span className="ml-2 px-2 py-0.5 rounded-full bg-white/10 text-[9px] tracking-[0.22em] uppercase">
              {inv.role_label}
            </span>
            {inv.engagement_type && inv.engagement_type !== "staff" && (
              <span className="ml-2 px-2 py-0.5 rounded-full bg-[#F5B82E]/15 text-[#F5B82E] text-[9px] tracking-[0.22em] uppercase">
                {inv.engagement_type}
              </span>
            )}
          </div>
          {inv.invitee_phone && (
            <div className="text-[10px] tracking-[0.18em] uppercase text-white/45 mt-0.5">{inv.invitee_phone}</div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={copy}
                  className="h-8 px-3 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-[10px] uppercase tracking-[0.22em]"
                  data-testid={`invite-copy-${inv.id}`}>
            {copied ? <Check size={12} className="mr-1" /> : <Copy size={12} className="mr-1" />}
            {copied ? "Copied" : "Copy link"}
          </Button>
          <a href={waUrl} target="_blank" rel="noopener noreferrer"
             className="h-8 px-3 rounded-full bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-400/30 text-emerald-200 text-[10px] uppercase tracking-[0.22em] inline-flex items-center"
             data-testid={`invite-whatsapp-${inv.id}`}>
            <MessageSquare size={12} className="mr-1" /> WhatsApp
          </a>
          <Button size="sm" onClick={revoke} variant="ghost"
                  className="h-8 w-8 p-0 text-white/45 hover:text-red-300 hover:bg-red-500/10"
                  data-testid={`invite-revoke-${inv.id}`}>
            <Trash2 size={14} />
          </Button>
        </div>
      </div>
    </li>
  );
}

function InviteDialog({ org, roles, onClose, onCreated }) {
  const [role, setRole]   = useState("");
  const [name, setName]   = useState("");
  const [phone, setPhone] = useState("");
  const [eng, setEng]     = useState("employee");
  const [busy, setBusy]   = useState(false);
  const [created, setCreated] = useState(null);

  const canSubmit = role && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/orgs/${org.id}/invites`, {
        role,
        invitee_name:    name.trim() || undefined,
        invitee_phone:   phone.trim() || undefined,
        engagement_type: eng,
      });
      const fullUrl = data.claim_url?.startsWith("http")
        ? data.claim_url
        : `${window.location.origin}${data.claim_url}`;
      setCreated({ ...data, claim_url: fullUrl });
      onCreated?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to create invite");
    } finally {
      setBusy(false);
    }
  };

  const reset = () => { setRole(""); setName(""); setPhone(""); setEng("employee"); setCreated(null); };

  if (created) {
    const wa = `https://wa.me/${(phone || "").replace(/[^0-9]/g, "")}?text=${encodeURIComponent(
      `You've been invited to join ${created.role_label} at ${org.brand_name || org.legal_name} on Call Sheet.\nOpen this link to set up your account:\n${created.claim_url}`
    )}`;
    return (
      <DialogContent className="bg-black border-white/15 text-white max-w-lg" data-testid="invite-created-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl uppercase tracking-tight">
            Invite ready
          </DialogTitle>
          <DialogDescription className="text-white/55">
            Share the link below — recipient will set their password and join as
            <span className="text-white"> {created.role_label}</span>.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 font-mono text-[12px] break-all text-white/80"
             data-testid="created-claim-url">
          {created.claim_url}
        </div>

        <div className="grid grid-cols-2 gap-3 mt-2">
          <Button onClick={() => { navigator.clipboard.writeText(created.claim_url); toast.success("Link copied"); }}
                  className="h-10 rounded-full text-[10px] uppercase tracking-[0.22em] btn-themed"
                  data-testid="dialog-copy-link">
            <Copy size={12} className="mr-1.5" /> Copy link
          </Button>
          <a href={wa} target="_blank" rel="noopener noreferrer"
             className="h-10 rounded-full inline-flex items-center justify-center bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-400/40 text-emerald-100 text-[10px] uppercase tracking-[0.22em]"
             data-testid="dialog-whatsapp-link">
            <MessageSquare size={12} className="mr-1.5" /> Open WhatsApp
          </a>
        </div>

        <div className="flex justify-between pt-2">
          <Button variant="ghost" onClick={reset} className="text-white/60 hover:text-white"
                  data-testid="invite-another-btn">
            Invite another
          </Button>
          <Button onClick={onClose} className="btn-themed rounded-full px-5 h-9 text-[10px] uppercase tracking-[0.22em]"
                  data-testid="invite-done-btn">
            Done
          </Button>
        </div>
      </DialogContent>
    );
  }

  return (
    <DialogContent className="bg-black border-white/15 text-white max-w-lg" data-testid="invite-dialog">
      <DialogHeader>
        <DialogTitle className="font-display text-2xl uppercase tracking-tight">
          Invite to {org.brand_name || org.legal_name}
        </DialogTitle>
        <DialogDescription className="text-white/55">
          Choose a role — the invitee will receive a WhatsApp-shareable link.
          Employee vs Freelancer matters for payroll &amp; tax flows.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4 mt-2">
        <div>
          <Label className="text-[10px] tracking-[0.25em] uppercase text-white/55">Role</Label>
          <div className="mt-2 grid grid-cols-2 gap-2" data-testid="invite-role-grid">
            {roles.map((r) => (
              <button key={r.key}
                      onClick={() => setRole(r.key)}
                      data-testid={`invite-role-${r.key}`}
                      className={`px-3 py-2.5 rounded-lg border text-left transition-all
                        ${role === r.key
                          ? "border-[#F5B82E]/60 bg-[#F5B82E]/10 text-white"
                          : "border-white/10 bg-white/[0.02] text-white/70 hover:border-white/25"}`}>
                <div className="text-sm font-medium">{r.label}</div>
                <div className="text-[10px] tracking-[0.18em] uppercase text-white/40 mt-0.5">
                  Rank {r.rank}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label className="text-[10px] tracking-[0.25em] uppercase text-white/55">Engagement</Label>
          <div className="mt-2 flex gap-2">
            {[
              { v: "employee",   l: "Employee" },
              { v: "freelancer", l: "Freelancer" },
              { v: "staff",      l: "Generic" },
            ].map((o) => (
              <button key={o.v}
                      onClick={() => setEng(o.v)}
                      data-testid={`invite-engagement-${o.v}`}
                      className={`flex-1 px-3 py-2 rounded-lg border text-[10px] tracking-[0.2em] uppercase
                        ${eng === o.v
                          ? "border-white/40 bg-white/10 text-white"
                          : "border-white/10 text-white/55 hover:border-white/25"}`}>
                {o.l}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] tracking-[0.25em] uppercase text-white/55">Name (optional)</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)}
                   placeholder="Asha Sharma"
                   className="mt-1.5 bg-black/30 border-white/15"
                   data-testid="invite-name-input" />
          </div>
          <div>
            <Label className="text-[10px] tracking-[0.25em] uppercase text-white/55">Phone</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)}
                   placeholder="+91 98765 00001"
                   className="mt-1.5 bg-black/30 border-white/15 font-mono"
                   data-testid="invite-phone-input" />
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-white/10 mt-2">
        <div className="text-[9px] tracking-[0.22em] uppercase text-white/40 flex items-center gap-1.5">
          <ShieldCheck size={11} className="text-[#F5B82E]" />
          Link valid 14 days · revokable any time
        </div>
        <Button onClick={submit} disabled={!canSubmit}
                className="h-10 px-5 rounded-full text-[10px] uppercase tracking-[0.22em] btn-themed"
                data-testid="create-invite-submit">
          {busy ? "Creating…" : <><Plus size={12} className="mr-1.5" /> Create link</>}
        </Button>
      </div>
    </DialogContent>
  );
}
